import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { streamSSE } from "@/lib/sse";
import { useAuth } from "@/context/AuthContext";
import { getLessonContext } from "@/data/curriculum";

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
const when = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };
const lessonName = (row) => {
  const c = getLessonContext(row.lesson_id);
  return c ? `${c.instrument.label} · ${c.course.title} · Lesson ${c.number}${row.title ? ` — ${row.title}` : ""}` : row.title || `Lesson ${row.lesson_id}`;
};

const Summary = ({ lessonId, testId }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setText("");
    try { await streamSSE("/admin/helper-report/summary", { lesson_id: lessonId || null }, (d) => setText((t) => t + d)); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      <button onClick={run} disabled={busy} data-testid={`${testId}-btn`}
        className="inline-flex items-center gap-1.5 rounded-full border border-primary px-4 py-1.5 text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {lessonId ? "Sum up sticking points" : "Sum up all sticking points"}
      </button>
      {text && <p className="mt-3 whitespace-pre-line rounded-2xl bg-primary/10 p-4 text-sm" data-testid={`${testId}-text`}>{text}</p>}
    </div>
  );
};

export default function AdminHelperReport() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { if (user?.role === "admin") api.get("/admin/helper-report").then(({ data }) => setD(data)).catch(() => setD({ lessons: [], total: 0 })); }, [user]);
  if (user && user.role !== "admin") return <div className="p-10 text-center">Admins only.</div>;
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-8" data-testid="admin-helper-report">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="helper-report-back"><ArrowLeft size={14} /> Admin</Link>
        <h1 className="mt-4 flex items-center gap-3 font-serif text-4xl"><MessageCircleQuestion className="text-primary" size={34} /> Helper questions</h1>
        <p className="mt-2 text-muted-foreground">What students ask the Music Helper, by lesson. Use it to spot where lessons could be clearer.</p>
        {!d ? <Loader2 className="mt-8 animate-spin text-primary" /> : (
          <>
            <p className="mt-6 text-sm" data-testid="helper-report-total"><span className="font-serif text-2xl">{d.total}</span> {d.total === 1 ? "question" : "questions"} across {plural(d.lessons.length, "lesson")}</p>
            {d.total > 0 && <Summary testId="helper-report-summary-all" />}
            {!d.lessons.length && <p className="mt-6 rounded-2xl border border-border p-6 text-sm text-muted-foreground" data-testid="helper-report-empty">No questions yet. They'll appear here once students start using the Music Helper.</p>}
            <div className="mt-8 space-y-4">
              {d.lessons.map((r, i) => (
                <section key={r.lesson_id} className="rounded-3xl border border-border bg-card p-6" data-testid={`helper-report-lesson-${i}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="font-serif text-lg">{lessonName(r)}</h2>
                    <p className="text-sm text-muted-foreground" data-testid={`helper-report-count-${i}`}>{plural(r.count, "question")} · {plural(r.students, "student")} · last {when(r.last)}</p>
                  </div>
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {r.questions.map((q, j) => <li key={j} className="rounded-xl bg-secondary px-3 py-2">{q.q}</li>)}
                  </ul>
                  <Summary lessonId={r.lesson_id} testId={`helper-report-summary-${i}`} />
                </section>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
